import { useMemo, useRef, useState, useLayoutEffect, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ShieldCheck, BadgeCheck, Truck, RefreshCw, Star, Leaf, Award } from 'lucide-react';
import { useTenant } from '../../contexts/TenantContext';
import { resolveFooterContent, pickFooterContent, formatCopyright, type FooterTrustBadge } from '../../utils/footerContent';
import { footerSocialIconFor } from './Footer';
import { getCachedStorefrontConfigSync } from '../../utils/storefrontConfig';
import { readableTextColor } from '../../utils/readableTextColor';
import { Editable } from '../storefront/inline/Editable';

// Footer links whose url is an in-page anchor (#slug) scroll to the matching
// section ([data-section-slug]) instead of routing — used by single-page
// templates so footer nav scrolls down the page rather than hitting a dead route.
const scrollToSectionAnchor = (e: React.MouseEvent, url: string) => {
  if (!url || !url.startsWith('#')) return;
  e.preventDefault();
  const slug = url.slice(1).toLowerCase();
  const el = document.querySelector(`[data-section-slug="${slug}"]`);
  if (el) (el as HTMLElement).scrollIntoView({ behavior: 'smooth', block: 'start' });
};

// Named-icon map for footer trust badges — keeps the row data-driven (the
// merchant picks an icon name in the builder) without shipping a raw component.
const TRUST_ICON: Record<string, React.ComponentType<any>> = {
  shield: ShieldCheck,
  check: BadgeCheck,
  truck: Truck,
  refresh: RefreshCw,
  star: Star,
  leaf: Leaf,
  award: Award,
};

/**
 * Guarantee/trust row. Desktop: 3 columns split by vertical dividers. Mobile:
 * a one-at-a-time snap carousel with pagination dots that auto-advances
 * (kaged.com register) — far livelier than three stacked blocks.
 */
const FooterTrustRow: React.FC<{
  badges: FooterTrustBadge[];
  nlInk: string;
  nlMuted: string;
  nlRule: string;
}> = ({ badges, nlInk, nlMuted, nlRule }) => {
  const scrollerRef = useRef<HTMLDivElement>(null);
  const [active, setActive] = useState(0);

  const handleScroll = () => {
    const el = scrollerRef.current;
    if (!el || !el.clientWidth) return;
    setActive(Math.round(el.scrollLeft / el.clientWidth));
  };

  const goTo = (i: number) => {
    const el = scrollerRef.current;
    if (el) el.scrollTo({ left: i * el.clientWidth, behavior: 'smooth' });
  };

  // Auto-advance — mobile only (desktop is a static grid), pauses for
  // prefers-reduced-motion.
  useEffect(() => {
    if (badges.length < 2) return;
    if (typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;
    const id = window.setInterval(() => {
      const el = scrollerRef.current;
      if (!el || window.innerWidth >= 640) return; // sm+ = grid, no auto-advance
      const next = (Math.round(el.scrollLeft / el.clientWidth) + 1) % badges.length;
      el.scrollTo({ left: next * el.clientWidth, behavior: 'smooth' });
    }, 3800);
    return () => window.clearInterval(id);
  }, [badges.length]);

  return (
    <div className="mb-16">
      <div
        ref={scrollerRef}
        onScroll={handleScroll}
        className="flex overflow-x-auto snap-x snap-mandatory hide-scrollbar sm:grid sm:grid-cols-3 sm:overflow-visible"
        style={{ borderBottom: `1px solid ${nlRule}` }}
      >
        {badges.map((b, i) => {
          const Ico = b.icon ? TRUST_ICON[b.icon.toLowerCase()] : undefined;
          return (
            <div
              key={`nl-trust-${i}`}
              className="shrink-0 basis-full sm:basis-auto snap-center flex flex-col items-center gap-2 text-center px-8 py-10 sm:border-l sm:first:border-l-0"
              style={{ borderColor: nlRule }}
            >
              {(Ico || b.icon) && (
                <span style={{ color: nlInk, lineHeight: 0, marginBottom: 4 }}>
                  {Ico ? <Ico size={22} strokeWidth={1.5} /> : <span style={{ fontSize: 20 }}>{b.icon}</span>}
                </span>
              )}
              <span
                style={{
                  color: nlInk,
                  fontSize: '0.82rem',
                  fontWeight: 600,
                  letterSpacing: '0.12em',
                  textTransform: 'uppercase',
                }}
              >
                {b.label}
              </span>
              {b.sublabel && (
                <span style={{ color: nlMuted, fontSize: '0.8rem', lineHeight: 1.55, maxWidth: '30ch' }}>
                  {b.sublabel}
                </span>
              )}
            </div>
          );
        })}
      </div>
      {badges.length > 1 && (
        <div className="flex sm:hidden justify-center items-center mt-6">
          {badges.map((_, i) => (
            <button
              key={`nl-trust-dot-${i}`}
              type="button"
              aria-label={`Go to guarantee ${i + 1}`}
              aria-current={i === active}
              onClick={() => goTo(i)}
              // 44px transparent hit area (touch target) wrapping a small visual dot.
              className="inline-flex items-center justify-center bg-transparent"
              style={{ width: 28, height: 28, padding: 0 }}
            >
              <span
                style={{
                  display: 'block',
                  width: i === active ? 22 : 7,
                  height: 7,
                  borderRadius: 9999,
                  background: i === active ? nlInk : `color-mix(in srgb, ${nlInk} 30%, transparent)`,
                  transition: 'width .35s ease, background .35s ease',
                }}
              />
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

/**
 * Shared newsletter-style footer used by Rouge / Luxe / Neon / Bloom / Kinetic /
 * Vital variants. Shape stays constant; colors + typography come from tenant
 * tokens (customColors / CSS vars) so picking any of these variants adapts to
 * the tenant palette automatically.
 */
export const NewsletterFooter: React.FC<{ hideNewsletterBand?: boolean; content?: any; forceWordmarkBand?: boolean }> = ({
  hideNewsletterBand = false,
  content,
  forceWordmarkBand = false,
}) => {
  const { tenant } = useTenant();
  const location = useLocation();
  const storefrontConfig = getCachedStorefrontConfigSync();
  const customColors = (storefrontConfig?.customColors || {}) as Record<string, string>;
  const primaryColor = customColors.primary || '#111111';
  const customLogo = (storefrontConfig?.customLogo as string) || '';
  const logo = customLogo || tenant?.branding?.logo || '';
  const companyName =
    storefrontConfig?.storeName ||
    tenant?.branding?.companyName ||
    tenant?.name ||
    storefrontConfig?.tenantName ||
    'Store';
  const currentYear = new Date().getFullYear();

  // Fallback columns when the store hasn't configured footer content. Kept to
  // routes that always exist so links never 404, while still looking fuller
  // than a lone "All products / Contact" pair.
  const seedFallback = [
    { title: 'Shop', links: [{ label: 'All products', url: '/products' }, { label: 'Cart', url: '/cart' }] },
    { title: 'Support', links: [{ label: 'Contact', url: '/contact' }, { label: 'Shipping & returns', url: '/contact' }] },
    { title: 'Legal', links: [{ label: 'Privacy', url: '/privacy' }, { label: 'Terms', url: '/terms' }] },
  ];

  const computedFooterContent = useMemo(
    () =>
      resolveFooterContent(pickFooterContent(storefrontConfig), {
        newsletter: {
          enabled: true,
          eyebrow: 'Stay in the loop',
          title: 'Get news, drops & offers',
          subtitle: 'Join the list — occasional emails, no spam. Unsubscribe anytime.',
          placeholder: 'Your email address',
          buttonLabel: 'Subscribe',
        },
        brandBio: '',
        menuColumns: seedFallback,
        socials: [],
        bottomLinks: [
          { label: 'Privacy Policy', url: '/privacy' },
          { label: 'Terms & Conditions', url: '/terms' },
        ],
        copyright: '© Copyright {year}. All rights reserved.',
      }),
    [storefrontConfig],
  );

  // When a layout passes its already-resolved footer content (rich, template-
  // specific defaults), use it — otherwise the footer falls back to the generic
  // seed content and looks sparse next to bespoke-footer templates.
  const footerContent = content || computedFooterContent;

  const theme = (footerContent.theme || {}) as Record<string, any>;
  const nlBgRaw = theme.bgColor || customColors.footerBg || customColors.text || '#111111';
  const nlInkRaw = theme.textColor || customColors.footerText || customColors.background || '#F5F5F5';
  // Invert knob — swap background/text so one footer covers both a light and a
  // dark (Depot-style) treatment without a separate variant.
  const nlBg = theme.invert === true ? nlInkRaw : nlBgRaw;
  const nlInk = theme.invert === true ? nlBgRaw : nlInkRaw;
  const nlAccent = theme.accentColor || customColors.primary || primaryColor || nlInk;
  const nlAccentLight = theme.accentLightColor || nlAccent;
  const nlMuted = theme.mutedTextColor || `color-mix(in srgb, ${nlInk} 65%, transparent)`;
  const nlRule = theme.borderColor || `color-mix(in srgb, ${nlInk} 18%, transparent)`;
  const nlRuleStrong = theme.borderActiveColor || nlRule;
  const headingFontFamily = theme.headingFontFamily || 'var(--font-heading, inherit)';
  const headingTextTransform = theme.headingTextTransform || 'uppercase';
  const headingLetterSpacing = theme.headingLetterSpacing || '0.2em';
  const headingFontWeight = theme.headingFontWeight ?? 600;
  const headingFontSize = theme.headingFontSize || '0.7rem';
  const linkFontFamily = theme.linkFontFamily || 'var(--font-body, inherit)';
  const linkTextTransform = theme.linkTextTransform || 'none';
  const linkLetterSpacing = theme.linkLetterSpacing || 'normal';
  const linkFontSize = theme.linkFontSize || '0.85rem';
  const brandFontFamily = theme.brandFontFamily || 'var(--font-heading, inherit)';
  const brandFontStyle = theme.brandFontStyle || 'normal';
  const brandTextTransform = theme.brandTextTransform || 'none';
  const brandLetterSpacing = theme.brandLetterSpacing || 'normal';
  const brandFontWeight = theme.brandFontWeight ?? 600;
  const showSocials = typeof theme.showSocials === 'boolean' ? theme.showSocials : true;
  const showNewsletterBand =
    typeof theme.showNewsletterBand === 'boolean' ? theme.showNewsletterBand : true;
  // Opt-in giant full-bleed wordmark band (Kaged-style) between the link columns
  // and the bottom bar. Off by default so other templates are untouched.
  const showWordmarkBand = theme.showWordmarkBand === true || forceWordmarkBand === true;
  const wordmarkText = theme.wordmarkText || companyName;
  // Giant wordmark reads best THIN + wide-tracked (Kaged register), not the
  // heavy display weight used elsewhere — so it has its own weight token.
  const wordmarkFontWeight = theme.wordmarkFontWeight ?? brandFontWeight;
  const wordmarkLetterSpacing = theme.wordmarkLetterSpacing || '0.04em';

  // Scale the giant wordmark so it always fills the viewport width edge-to-edge,
  // regardless of how long the store name is (Kaged-style). Pure-CSS vw sizing
  // only works for short names; long ones overflow/clip — so measure + fit.
  const wordmarkRef = useRef<HTMLSpanElement>(null);
  const [wordmarkSize, setWordmarkSize] = useState<number>(0);
  useLayoutEffect(() => {
    if (!showWordmarkBand) return;
    const el = wordmarkRef.current;
    if (!el) return;
    const fit = () => {
      const probe = 200; // measure at a known size, then scale to fill
      el.style.fontSize = `${probe}px`;
      const natural = el.scrollWidth;
      if (!natural) return;
      // Leave a small side gutter (Kaged doesn't bleed the wordmark off-screen).
      const target = (typeof window !== 'undefined' ? window.innerWidth : 1280) * 0.94;
      let next = Math.max(40, Math.min((probe * target) / natural, 300));
      el.style.fontSize = `${next}px`;
      // Correction pass: real font metrics (weight/letter-spacing) can differ
      // from the probe, so re-measure at the applied size and shrink if the text
      // still overflows the target — guarantees it never clips off-screen.
      const actual = el.scrollWidth;
      if (actual > target) {
        next = Math.max(40, (next * target) / actual);
        el.style.fontSize = `${next}px`;
      }
      setWordmarkSize(next);
    };
    fit();
    // Re-fit once webfonts load — measuring against the fallback face picks the
    // wrong size and the real (wider) face then clips off-screen.
    if (typeof document !== 'undefined' && (document as any).fonts?.ready) {
      (document as any).fonts.ready.then(fit).catch(() => {});
    }
    window.addEventListener('resize', fit);
    return () => window.removeEventListener('resize', fit);
  }, [showWordmarkBand, wordmarkText]);
  // Editorial/airy treatment — underlined inputs, text-link submit, generous
  // whitespace (vs the default boxed input + solid button). Opt-in per layout.
  const minimal = theme.minimal === true;
  // Band (Kaged-style) mode integrates the newsletter + link columns into one
  // balanced row and uses rounded pill fields — a distinct, fuller treatment
  // for the giant-wordmark footer (Vital) vs the other variants.
  const bandMode = showWordmarkBand;
  const nlFieldStyle: React.CSSProperties = bandMode
    ? { flex: 1, minWidth: 200, padding: '15px 22px', background: 'transparent', border: `1px solid ${nlRule}`, borderRadius: 9999, color: nlInk, fontSize: '0.9rem', outline: 'none' }
    : minimal
    ? { flex: 1, minWidth: 200, padding: '12px 2px', background: 'transparent', border: 'none', borderBottom: `1px solid ${nlRule}`, borderRadius: 0, color: nlInk, fontSize: '0.92rem', outline: 'none' }
    : { flex: 1, minWidth: 200, padding: '12px 14px', background: 'transparent', border: `1px solid ${nlRule}`, color: nlInk, fontSize: '0.85rem', outline: 'none' };

  // Avoid two stacked subscribe forms. The home/single-page layouts render this
  // footer directly (no prop), so self-detect: when the current page already
  // renders a dedicated `newsletter` section, drop the footer's band. Check ALL
  // identifying fields joined (the newsletter section's category is "cta" while
  // its name/slug carry "newsletter"). Footer.tsx also passes `hideNewsletterBand`
  // for inner pages.
  const pathname = location.pathname.toLowerCase();
  const isHomePage = pathname === '' || pathname === '/' || pathname === '/home';
  const pageSections: any[] = Array.isArray(storefrontConfig?.customSections)
    ? storefrontConfig.customSections
    : Array.isArray((storefrontConfig as any)?.sections)
      ? (storefrontConfig as any).sections
      : [];
  const pageHasNewsletterSection = pageSections.some((s: any) => {
    const key = (typeof s === 'string'
      ? s
      : [s?.category, s?.type, s?.sectionSlug, s?.slug, s?.name].filter(Boolean).join(' ')
    ).toLowerCase();
    return key.includes('newsletter');
  });

  const newsletterEnabled =
    showNewsletterBand &&
    footerContent.newsletter?.enabled !== false &&
    !hideNewsletterBand &&
    !(isHomePage && pageHasNewsletterSection);

  // Guarantee/trust row (Kaged-style) — opt-in via footerContent.trustBadges.
  // Renders above the columns in both the band and standard layouts.
  const trustBadges: FooterTrustBadge[] = Array.isArray(footerContent.trustBadges)
    ? footerContent.trustBadges
    : [];
  const trustRow =
    trustBadges.length > 0 ? (
      <FooterTrustRow badges={trustBadges} nlInk={nlInk} nlMuted={nlMuted} nlRule={nlRule} />
    ) : null;

  // Social-icon row — shared so both the band layout (Vital) and the standard
  // layout render it (previously the band layout dropped socials entirely).
  const socialsNode =
    showSocials && Array.isArray(footerContent.socials) && footerContent.socials.length > 0 ? (
      <div className="flex flex-wrap items-center gap-3">
        {footerContent.socials.map((s, idx) => {
          const label = s.label || s.platform.charAt(0).toUpperCase() + s.platform.slice(1);
          const isExternal = /^https?:\/\//i.test(s.url);
          return (
            <a
              key={`nl-soc-${idx}`}
              href={s.url}
              target={isExternal ? '_blank' : undefined}
              rel={isExternal ? 'noopener noreferrer' : undefined}
              aria-label={label}
              className="w-9 h-9 rounded-full flex items-center justify-center transition-opacity hover:opacity-70"
              style={{ border: `1px solid ${nlRule}`, color: nlInk }}
            >
              {footerSocialIconFor(s)}
            </a>
          );
        })}
      </div>
    ) : null;

  return (
    <footer
      style={{
        background: nlBg,
        color: nlInk,
        fontFamily: 'var(--font-body, inherit)',
        // When the newsletter band is hidden the footer butts up against the
        // page's own newsletter section (same dark bg) — a hairline keeps it
        // from floating in a void.
        borderTop: newsletterEnabled ? undefined : `1px solid ${nlRule}`,
      }}
    >
      {newsletterEnabled && !bandMode && (
        <div style={{ borderBottom: `1px solid ${nlRule}`, padding: minimal ? '72px 0' : '56px 0' }}>
          <div className={`container mx-auto px-4 flex flex-col md:flex-row md:items-center ${minimal ? 'gap-10 md:gap-16' : 'gap-6 md:gap-10'}`}>
            <div style={{ flex: '1 1 320px', maxWidth: '46ch' }}>
              {footerContent.newsletter?.eyebrow && (
                <p
                  style={{
                    fontSize: '0.7rem',
                    letterSpacing: '0.25em',
                    textTransform: 'uppercase',
                    color: nlAccent,
                    marginBottom: 6,
                  }}
                >
                  {footerContent.newsletter.eyebrow}
                </p>
              )}
              {footerContent.newsletter?.title && (
                <h3
                  style={{
                    fontFamily: brandFontFamily,
                    fontStyle: brandFontStyle as any,
                    fontSize: 'clamp(1.4rem, 2.4vw, 1.9rem)',
                    fontWeight: brandFontWeight as any,
                    letterSpacing: brandLetterSpacing,
                    textTransform: brandTextTransform as any,
                    lineHeight: 1.2,
                    color: nlInk,
                  }}
                >
                  {footerContent.newsletter.title}
                </h3>
              )}
              {footerContent.newsletter?.subtitle && (
                <p style={{ marginTop: 8, fontSize: '0.85rem', lineHeight: 1.6, color: nlMuted }}>
                  {footerContent.newsletter.subtitle}
                </p>
              )}
            </div>
            <form
              onSubmit={(e) => e.preventDefault()}
              className="flex gap-2"
              style={{ flex: '1 1 360px', maxWidth: 480, flexWrap: 'wrap' }}
            >
              <input
                type="email"
                placeholder={footerContent.newsletter?.placeholder || 'your@email.com'}
                style={nlFieldStyle}
              />
              {(footerContent.newsletter as any)?.showPhone && (
                <input
                  type="tel"
                  aria-label="Phone number"
                  placeholder={(footerContent.newsletter as any)?.phonePlaceholder || 'Phone number (SMS)'}
                  style={nlFieldStyle}
                />
              )}
              <button
                type="submit"
                style={
                  minimal
                    ? {
                        padding: '12px 2px',
                        background: 'transparent',
                        color: nlAccent,
                        border: 'none',
                        fontSize: '0.82rem',
                        fontWeight: 600,
                        letterSpacing: '0.12em',
                        textTransform: 'uppercase',
                        textDecoration: 'underline',
                        textUnderlineOffset: '6px',
                        cursor: 'pointer',
                        whiteSpace: 'nowrap',
                      }
                    : {
                        padding: '12px 22px',
                        background: nlAccent,
                        color: readableTextColor(nlAccent),
                        border: 'none',
                        fontSize: '0.78rem',
                        fontWeight: 700,
                        letterSpacing: '0.08em',
                        textTransform: 'uppercase',
                        cursor: 'pointer',
                      }
                }
              >
                {footerContent.newsletter?.buttonLabel || 'Subscribe'}
              </button>
            </form>
          </div>
        </div>
      )}

      <div
        className={
          bandMode
            ? 'mx-auto w-full max-w-[1720px] px-6 sm:px-10 lg:px-16 pt-20 pb-16'
            : 'container mx-auto px-4 pt-20 pb-16'
        }
      >
        {trustRow}
        {bandMode ? (
          /* Kaged-style balanced row: newsletter (left) + link columns spread
             across the width. Fills the row edge-to-edge instead of a bare
             centered input + a dead left gap. */
          <div className="grid grid-cols-1 gap-x-12 gap-y-12 lg:grid-cols-[1.9fr_1fr_1fr_1fr] lg:items-start">
            <div style={{ maxWidth: '40ch' }}>
              {newsletterEnabled && (
                <>
                  {footerContent.newsletter?.eyebrow && (
                    <p style={{ fontSize: '0.7rem', letterSpacing: '0.25em', textTransform: 'uppercase', color: nlMuted, marginBottom: 14 }}>
                      {footerContent.newsletter.eyebrow}
                    </p>
                  )}
                  {footerContent.newsletter?.title && (
                    <h3 style={{ fontFamily: brandFontFamily, fontStyle: brandFontStyle as any, fontSize: 'clamp(1.5rem, 2.2vw, 2rem)', fontWeight: brandFontWeight as any, letterSpacing: brandLetterSpacing, textTransform: brandTextTransform as any, lineHeight: 1.12, color: nlInk, marginBottom: 20 }}>
                      {footerContent.newsletter.title}
                    </h3>
                  )}
                  <form onSubmit={(e) => e.preventDefault()} className="flex flex-wrap gap-3" style={{ maxWidth: 460 }}>
                    <input type="email" placeholder={footerContent.newsletter?.placeholder || 'Your email address'} style={nlFieldStyle} />
                    <button
                      type="submit"
                      style={{ padding: '15px 30px', background: nlInk, color: nlBg, border: 'none', borderRadius: 9999, fontSize: '0.78rem', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', cursor: 'pointer', whiteSpace: 'nowrap' }}
                    >
                      {footerContent.newsletter?.buttonLabel || 'Subscribe'}
                    </button>
                  </form>
                  {footerContent.newsletter?.subtitle && (
                    <p style={{ marginTop: 16, fontSize: '0.8rem', lineHeight: 1.6, color: nlMuted, maxWidth: '38ch' }}>
                      {footerContent.newsletter.subtitle}
                    </p>
                  )}
                </>
              )}
              {socialsNode && <div className="mt-7">{socialsNode}</div>}
            </div>
            {(footerContent.menuColumns || []).slice(0, 3).map((section, sectionIdx) => (
              <div key={`nlb-col-${sectionIdx}`}>
                <p style={{ fontFamily: headingFontFamily, fontSize: headingFontSize, letterSpacing: headingLetterSpacing, textTransform: headingTextTransform as any, fontWeight: headingFontWeight as any, color: nlMuted, marginBottom: 16 }}>
                  {section.title}
                </p>
                <ul className="space-y-3">
                  {section.links.map((link, linkIdx) => {
                    const isExternal = /^https?:\/\//i.test(link.url);
                    const isAnchor = (link.url || '').startsWith('#');
                    const linkStyle = { color: nlInk, fontFamily: linkFontFamily, fontSize: linkFontSize, textTransform: linkTextTransform as any, letterSpacing: linkLetterSpacing };
                    return (
                      <li key={`nlb-link-${sectionIdx}-${linkIdx}`}>
                        {isExternal ? (
                          <a href={link.url} target="_blank" rel="noopener noreferrer" style={linkStyle} className="hover:opacity-80" onMouseEnter={(e) => (e.currentTarget.style.color = nlAccentLight)} onMouseLeave={(e) => (e.currentTarget.style.color = nlInk)}>{link.label}</a>
                        ) : isAnchor ? (
                          // Single-page templates: scroll to the section (data-section-slug) instead of routing.
                          <a href={link.url} onClick={(e) => scrollToSectionAnchor(e, link.url)} style={linkStyle} className="hover:opacity-80" onMouseEnter={(e) => (e.currentTarget.style.color = nlAccentLight)} onMouseLeave={(e) => (e.currentTarget.style.color = nlInk)}>{link.label}</a>
                        ) : (
                          <Link to={link.url || '#'} style={linkStyle} className="hover:opacity-80" onMouseEnter={(e) => (e.currentTarget.style.color = nlAccentLight)} onMouseLeave={(e) => (e.currentTarget.style.color = nlInk)}>{link.label}</Link>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))}
          </div>
        ) : (
        <div className={(footerContent.menuColumns || []).length > 0 ? 'flex flex-col gap-10 md:flex-row md:items-start md:gap-24' : 'flex flex-col items-center text-center gap-4'}>
          <div className={(footerContent.menuColumns || []).length > 0 ? 'md:flex-1 max-w-sm' : 'max-w-xl'}>
            <div className={`flex items-center gap-3 mb-3 ${(footerContent.menuColumns || []).length > 0 ? '' : 'justify-center'}`}>
              {logo ? (
                <img src={logo} alt={companyName} className="h-10 w-auto object-contain" />
              ) : (
                <span
                  style={{
                    fontFamily: 'var(--font-heading, inherit)',
                    fontSize: '1.25rem',
                    fontWeight: 600,
                    color: nlInk,
                  }}
                >
                  <Editable as="span" field="footerContent.brandText" value={companyName} />
                </span>
              )}
            </div>
            {footerContent.brandBio && (
              <p style={{ fontSize: '0.85rem', lineHeight: 1.6, color: nlMuted, maxWidth: '36ch' }}>
                <Editable as="span" field="footerContent.brandBio" value={footerContent.brandBio} />
              </p>
            )}
            {showSocials && Array.isArray(footerContent.socials) && footerContent.socials.length > 0 && (
              <div className={`mt-4 flex flex-wrap items-center gap-3 ${(footerContent.menuColumns || []).length > 0 ? '' : 'justify-center'}`}>
                {footerContent.socials.map((s, idx) => {
                  const label = s.label || s.platform.charAt(0).toUpperCase() + s.platform.slice(1);
                  const isExternal = /^https?:\/\//i.test(s.url);
                  return (
                    <a
                      key={`nl-soc-${idx}`}
                      href={s.url}
                      target={isExternal ? '_blank' : undefined}
                      rel={isExternal ? 'noopener noreferrer' : undefined}
                      aria-label={label}
                      className="w-9 h-9 rounded-full flex items-center justify-center transition-opacity"
                      style={{ border: `1px solid ${nlRule}`, color: nlInk }}
                    >
                      {footerSocialIconFor(s)}
                    </a>
                  );
                })}
              </div>
            )}
          </div>
          {(footerContent.menuColumns || []).length > 0 && (
          <div className="flex flex-wrap gap-12 md:gap-16">
          {(footerContent.menuColumns || []).slice(0, 4).map((section, sectionIdx) => (
            <div key={`nl-col-${sectionIdx}`} className="min-w-[130px]">
              <p
                style={{
                  fontFamily: headingFontFamily,
                  fontSize: headingFontSize,
                  letterSpacing: headingLetterSpacing,
                  textTransform: headingTextTransform as any,
                  fontWeight: headingFontWeight as any,
                  color: nlMuted,
                  marginBottom: 18,
                }}
              >
                {section.title}
              </p>
              <ul className="space-y-3.5">
                {section.links.map((link, linkIdx) => {
                  const isExternal = /^https?:\/\//i.test(link.url);
                  const linkStyle = {
                    color: nlInk,
                    fontFamily: linkFontFamily,
                    fontSize: linkFontSize,
                    textTransform: linkTextTransform as any,
                    letterSpacing: linkLetterSpacing,
                  };
                  return (
                    <li key={`nl-link-${sectionIdx}-${linkIdx}`}>
                      {isExternal ? (
                        <a
                          href={link.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={linkStyle}
                          className="hover:opacity-80"
                          onMouseEnter={(e) => (e.currentTarget.style.color = nlAccentLight)}
                          onMouseLeave={(e) => (e.currentTarget.style.color = nlInk)}
                        >
                          {link.label}
                        </a>
                      ) : (
                        <Link
                          to={link.url || '#'}
                          style={linkStyle}
                          className="hover:opacity-80"
                          onMouseEnter={(e) => (e.currentTarget.style.color = nlAccentLight)}
                          onMouseLeave={(e) => (e.currentTarget.style.color = nlInk)}
                        >
                          {link.label}
                        </Link>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
          </div>
          )}
        </div>
        )}

        {showWordmarkBand && (
          <div
            aria-hidden="true"
            className="mt-20 select-none"
            style={{
              // Break out of the container to span the full viewport width.
              marginLeft: 'calc(50% - 50vw)',
              marginRight: 'calc(50% - 50vw)',
              width: '100vw',
              maxWidth: '100vw',
              overflow: 'hidden',
            }}
          >
            <span
              ref={wordmarkRef}
              style={{
                display: 'block',
                width: '100%',
                textAlign: 'center',
                fontFamily: brandFontFamily,
                fontWeight: wordmarkFontWeight as any,
                fontStyle: brandFontStyle as any,
                letterSpacing: wordmarkLetterSpacing,
                textTransform: 'uppercase',
                color: nlInk,
                fontSize: wordmarkSize ? `${wordmarkSize}px` : 'clamp(3.2rem, 15vw, 14rem)',
                lineHeight: 0.9,
                whiteSpace: 'nowrap',
              }}
            >
              {wordmarkText}
            </span>
          </div>
        )}

        <div
          className="mt-16 pt-8 flex flex-col md:flex-row md:items-center md:justify-between gap-4"
          style={{ borderTop: `1px solid ${nlRuleStrong}` }}
        >
          <p style={{ fontSize: '0.78rem', color: nlMuted }}>
            <Editable as="span" field="footerContent.copyright" value={formatCopyright(footerContent.copyright, companyName, currentYear)} />
          </p>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1" style={{ fontSize: '0.78rem', color: nlMuted }}>
            {(footerContent.bottomLinks && footerContent.bottomLinks.length > 0
              ? footerContent.bottomLinks
              : [
                  { label: 'Privacy Policy', url: '/privacy' },
                  { label: 'Terms & Conditions', url: '/terms' },
                ]
            ).map((link, idx, arr) => {
              const isExternal = /^https?:\/\//i.test(link.url);
              return (
                <span key={`nl-bottom-${idx}`} className="flex items-center gap-3">
                  {isExternal ? (
                    <a href={link.url} target="_blank" rel="noopener noreferrer" className="hover:opacity-100">
                      {link.label}
                    </a>
                  ) : (
                    <Link to={link.url || '#'} className="hover:opacity-100">
                      {link.label}
                    </Link>
                  )}
                  {idx < arr.length - 1 && <span style={{ opacity: 0.5 }}>·</span>}
                </span>
              );
            })}
          </div>
        </div>
      </div>
    </footer>
  );
};

export default NewsletterFooter;
